import type { OPCS4Code } from "@/clinical-codes/types/ClinicalCodeTypes";
import { useSearchOpcs4Codes } from "@/clinical-codes/useSearchOpcs4Codes";
import type { OpcsVersion } from "@/clinical-codes/opcsVersion";
import { getOpcsVersionLabel } from "@/clinical-codes/opcsVersion";
import { Label } from "@/components/ui/label";
import { DragDropContext, Draggable, Droppable, type DropResult } from "@hello-pangea/dnd";
import { useState } from "react";

interface ProceduresSectionProps {
  selectedProcedures: OPCS4Code[];
  onProceduresChange: (procedures: OPCS4Code[]) => void;
  onFormChange: () => void;
  opcsVersion?: OpcsVersion;
}

export function ProceduresSection({
  selectedProcedures,
  onProceduresChange,
  onFormChange,
  opcsVersion,
}: ProceduresSectionProps) {
  const [proceduresSearchTerm, setProceduresSearchTerm] = useState("");

  const { codes: procedureCodes } = useSearchOpcs4Codes(proceduresSearchTerm, undefined, opcsVersion);

  const addProcedure = (code: OPCS4Code) => {
    // Allow multiple instances of the same procedure code
    // Uniqueness is based on position in the list, not just the code
    // This allows users to add the same procedure code multiple times
    // with different positions in the list
    onProceduresChange([...selectedProcedures, code]);
    onFormChange();
  };

  const removeProcedure = (_id: string, index: number) => {
    const newProcedures = [...selectedProcedures];
    newProcedures.splice(index, 1);
    onProceduresChange(newProcedures);
    onFormChange();
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;
    const items = Array.from(selectedProcedures);
    const [removed] = items.splice(source.index, 1);
    items.splice(destination.index, 0, removed);
    onProceduresChange(items);
    onFormChange();
  };

  return (
    <div className="space-y-2 w-full">
      <Label>Procedures ({opcsVersion ? getOpcsVersionLabel(opcsVersion) : "OPCS-4"})</Label>
      <div className="w-full px-1 mb-2 relative">
        <input
          type="text"
          className="w-full border rounded px-2 py-1"
          placeholder="Search procedures..."
          value={proceduresSearchTerm}
          onChange={(e) => setProceduresSearchTerm(e.target.value)}
        />
        {proceduresSearchTerm && (
          <div className="absolute left-0 right-0 bg-white border rounded shadow mt-1 max-h-40 overflow-y-auto z-50">
            {procedureCodes
              ?.filter(
                (code) =>
                  code.code.toLowerCase().includes(proceduresSearchTerm.toLowerCase()) ||
                  code.description.toLowerCase().includes(proceduresSearchTerm.toLowerCase()),
              )
              .slice(0, 10)
              .map((code) => (
                <div
                  key={code.id}
                  className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    addProcedure(code);
                    setProceduresSearchTerm("");
                  }}
                >
                  {code.code} - {code.description}
                </div>
              ))}
          </div>
        )}
      </div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="procedures-droppable" type="procedures">
          {(provided) => (
            <ul
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="bg-gray-50 rounded border min-h-[40px] p-2"
            >
              {selectedProcedures.map((code, idx) => (
                <Draggable key={`${code.id}-${idx}`} draggableId={`${code.id}-${idx}`} index={idx}>
                  {(provided, snapshot) => (
                    <li
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`flex items-center justify-between p-3 mb-1 rounded shadow-sm ${
                        snapshot.isDragging ? "ring-2 ring-blue-400" : ""
                      }`}
                    >
                      <span {...provided.dragHandleProps} className="cursor-move mr-2">
                        ☰
                      </span>
                      <span className="flex-1">
                        {code.code} - {code.description}
                      </span>
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          className="ml-2 text-red-500 hover:text-red-700"
                          onClick={() => removeProcedure(code.id, idx)}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </ul>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}
